import { useMemo, useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  useDroppable,
  closestCorners,
  type DragStartEvent,
  type DragEndEvent,
} from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { cn } from '@/lib/utils';
import type { TodoItemResponse, TodoProjectResponse } from '@/lib/api';
import { TodoItemRow } from './TodoItemRow';
import { useSectionCollapse } from '../lib/collapsedSections';
import { resolveProjectColor } from '../lib/projectColors';

const NO_PROJECT = '__none__';
const containerId = (key: string) => `proj:${key}`;
const parseContainer = (id: string): string | null =>
  id.startsWith('proj:') ? id.slice('proj:'.length) : null;

interface ProjectGroupedViewProps {
  /** Active, already-filtered top-level items. */
  items: TodoItemResponse[];
  projects: TodoProjectResponse[];
  canEditItem: (item: TodoItemResponse) => boolean;
  isManager: boolean;
  /** Reassign a task's project (null = No project) — fired on drag between groups. */
  onReassignProject: (itemId: string, projectId: string | null) => void;
}

/** One collapsible project group; also a drop target for drag-to-reassign. */
function ProjectGroup({
  groupKey,
  label,
  color,
  items,
  canEditItem,
  isManager,
}: {
  groupKey: string;
  label: string;
  color: string | null;
  items: TodoItemResponse[];
  canEditItem: (item: TodoItemResponse) => boolean;
  isManager: boolean;
}) {
  const { expanded, toggle } = useSectionCollapse(`project:${groupKey}`);
  const { setNodeRef, isOver } = useDroppable({ id: containerId(groupKey) });
  const dot = resolveProjectColor(color);

  return (
    <div
      className={cn(
        'rounded-lg border p-3',
        isOver ? 'border-primary bg-primary/5' : 'border-border bg-card',
      )}
    >
      <button
        type="button"
        onClick={toggle}
        className="flex w-full items-center gap-1.5 py-1 text-left hover:text-primary"
        aria-expanded={expanded}
        aria-label={`${expanded ? 'Collapse' : 'Expand'} project "${label}"`}
      >
        {expanded ? (
          <ChevronDown className="h-4 w-4 flex-shrink-0" />
        ) : (
          <ChevronRight className="h-4 w-4 flex-shrink-0" />
        )}
        {groupKey !== NO_PROJECT && (
          <span className={cn('h-2.5 w-2.5 flex-shrink-0 rounded-full', dot.dot)} />
        )}
        <span className="truncate text-sm font-medium text-muted-foreground">{label}</span>
        <span className="text-xs text-muted-foreground/60">{items.length}</span>
      </button>

      {expanded && (
        <div ref={setNodeRef} className="mt-1 min-h-[12px]">
          <SortableContext
            items={items.map((i) => i.itemId)}
            strategy={verticalListSortingStrategy}
          >
            {items.map((item) => (
              <TodoItemRow
                key={item.itemId}
                item={item}
                canEdit={canEditItem(item)}
                canManageSection={isManager}
                groupContext="project"
              />
            ))}
          </SortableContext>
          {items.length === 0 && (
            <p className="px-3 py-2 text-xs text-muted-foreground/70">Drop tasks here</p>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * The list grouped by PROJECT instead of section — the same tasks under a
 * different lens. Every project (plus a "No project" bucket) is a collapsible
 * group and a drop target: dragging a task into another group reassigns its
 * project. Reordering within a group is out of scope (that lives in the section
 * view); this view is for grouping + quick reassignment.
 */
export function ProjectGroupedView({
  items,
  projects,
  canEditItem,
  isManager,
  onReassignProject,
}: ProjectGroupedViewProps) {
  const [activeItem, setActiveItem] = useState<TodoItemResponse | null>(null);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 5 } }),
  );

  const itemsByProject = useMemo(() => {
    const map = new Map<string, TodoItemResponse[]>();
    map.set(NO_PROJECT, []);
    for (const p of projects) map.set(p.projectId, []);
    for (const item of items) {
      const key = item.projectId && map.has(item.projectId) ? item.projectId : NO_PROJECT;
      map.get(key)!.push(item);
    }
    for (const list of map.values()) list.sort((a, b) => a.sortOrder - b.sortOrder);
    return map;
  }, [items, projects]);

  const projectOfItem = useMemo(() => {
    const m = new Map<string, string>();
    for (const item of items) {
      m.set(
        item.itemId,
        item.projectId && itemsByProject.has(item.projectId) ? item.projectId : NO_PROJECT,
      );
    }
    return m;
  }, [items, itemsByProject]);

  function handleDragEnd(e: DragEndEvent) {
    setActiveItem(null);
    const { active, over } = e;
    if (!over) return;
    const itemId = String(active.id);
    // The drop target is either a group container or another item — resolve to a
    // project key either way.
    const overId = String(over.id);
    const targetKey = parseContainer(overId) ?? projectOfItem.get(overId) ?? null;
    if (targetKey === null) return;
    const currentKey = projectOfItem.get(itemId) ?? NO_PROJECT;
    if (targetKey === currentKey) return;
    onReassignProject(itemId, targetKey === NO_PROJECT ? null : targetKey);
  }

  // Order: "No project" first, then projects in their sort order.
  const groups: Array<{ key: string; label: string; color: string | null }> = [
    { key: NO_PROJECT, label: 'No project', color: null },
    ...projects.map((p) => ({ key: p.projectId, label: p.name, color: p.color })),
  ];

  const hasAny = items.length > 0;

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCorners}
      onDragStart={(e: DragStartEvent) =>
        setActiveItem(items.find((i) => i.itemId === String(e.active.id)) ?? null)
      }
      onDragEnd={handleDragEnd}
      onDragCancel={() => setActiveItem(null)}
    >
      <div className="space-y-3">
        {groups.map((g) => (
          <ProjectGroup
            key={g.key}
            groupKey={g.key}
            label={g.label}
            color={g.color}
            items={itemsByProject.get(g.key) ?? []}
            canEditItem={canEditItem}
            isManager={isManager}
          />
        ))}
        {!hasAny && (
          <div className="py-12 text-center text-muted-foreground">
            <p>No tasks yet. Add tasks and assign them to a project.</p>
          </div>
        )}
      </div>
      <DragOverlay>
        {activeItem ? (
          <div className="rounded-md border bg-background px-3 py-2 text-sm shadow-lg">
            {activeItem.title}
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}
